import { useMemo } from 'react'
import {
  type AccountData,
  type PinDisplayProps,
  type UserProfileData,
  type Workflow,
  isNotDefined,
  getWorkflowAccountLastActivity,
  isDefined,
} from '@terros/common'
import { useProfile } from '../../user'
import { getHistoryCutoffDate } from './getHistoryCutoffDate'

type GetAccountDisplayPropsInput = {
  workflow: Workflow
  account: Pick<AccountData, 'workflowHistory' | 'tags' | 'ownerHistory'> | undefined
  user: Pick<UserProfileData, 'userId' | 'permissionMap' | 'roles'>
  shouldIncludeTags?: boolean
}

export const getAccountDisplayPropsForUser = ({
  workflow,
  account,
  user,
  shouldIncludeTags,
}: GetAccountDisplayPropsInput): PinDisplayProps => {
  if (isNotDefined(account)) return { type: 'initial' }

  const lastActivity = getWorkflowAccountLastActivity(account, shouldIncludeTags)
  if (isNotDefined(lastActivity)) return { type: 'initial' }

  const cutoffDate = getHistoryCutoffDate({ workflow, account, user })
  const shouldHideAction = isDefined(cutoffDate) && lastActivity.timestamp <= cutoffDate

  return workflow.getActivityDataDisplayProps({
    stageId: lastActivity.stageId,
    actionId: shouldHideAction ? undefined : lastActivity.actionId,
  })
}

type UseAccountDisplayPropsInput = {
  workflow: Workflow | undefined
  account: Pick<AccountData, 'workflowHistory' | 'tags' | 'ownerHistory'> | undefined
  shouldIncludeTags?: boolean
}

export const useAccountDisplayProps = ({
  workflow,
  account,
  shouldIncludeTags,
}: UseAccountDisplayPropsInput): PinDisplayProps => {
  const { user } = useProfile()
  return useMemo(() => {
    if (isNotDefined(workflow) || isNotDefined(account)) return { type: 'initial' }
    if (isNotDefined(user)) return workflow.getAccountDisplayProps(account, shouldIncludeTags)
    return getAccountDisplayPropsForUser({ workflow, account, user, shouldIncludeTags })
  }, [workflow, account, shouldIncludeTags, user])
}
